<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useI18nRoute } from '@/composables/useI18nRoute'
import UiIcon from '@/components/common/UiIcon.vue'
import { withUtmParams } from '@/utils/utm'

const { t, locale } = useI18n()
const { localizedPath } = useI18nRoute()

const privacyPath = computed(() =>
  localizedPath('page', {
    slug: String(locale.value).startsWith('en') ? 'privacy' : 'personvern',
  }),
)

function footerHref(href: string) {
  return withUtmParams(href, {
    utm_source: 'footer_navigation',
    utm_medium: 'webpage',
    utm_campaign: 'test_suite_navigation',
  })
}

const currentYear = computed(() => new Date().getFullYear())
const buildVersion = import.meta.env.VITE_APP_BUILD_VERSION?.trim() ?? ''
</script>

<template>
  <footer itemscope itemtype="https://schema.org/WPFooter"
    class="footer-btm relative self-end w-full px-6 py-12 lg:py-16 xl:py-32 bg-surface text-base-content">


    <div class="footer-content w-full flex flex-col lg:flex-row lg:flex-wrap lg:gap-8 mx-auto max-w-screen-2xl">

      <div class="w-full lg:flex-1 lg:basis-col-2 order-1 lg:order-0 space-y-4 relative mt-8 lg:mt-0">

        <div class="max-w-prose space-y-2">
          <h2 itemprop="headline" class="text-lg lg:text-lg xl:text-xl font-bold text-primary-deep">{{ currentYear }}
            &copy; EmmaSofia AS</h2>
          <i18n-t keypath="footer.poweredBy" tag="p" itemprop="text" class="text-base">
            <template #company>
              <a class="text-foreground/90 hover:text-primary-deep underline hover:no-underline"
                :href="footerHref('https://emmasofia.no')" target="_blank" rel="noopener">EmmaSofia</a>
            </template>
          </i18n-t>
          <p itemprop="text" class="text-base">{{ t('footer.disclaimer') }}</p>

        </div>

      </div>

      <div class="w-full lg:flex-1 lg:basis-col-2 md:flex md-flex-row md:gap-4 flex-wrap space-y-8 md:space-y-0">

        <div class="order-last lg:order-first md:w-1/2">

          <nav class="flex-1">
            <ul class="space-y-2">
              <li>
                <h2 itemprop="headline" class="font-bold text-lg text-primary-deep">{{ t('footer.editor') }}</h2>
              </li>
              <li>
                <a class="text-foreground/90 hover:text-primary-deep underline hover:no-underline"
                  :href="footerHref('https://www.emmasofia.no/behandlere/paal-orjan-johansen/')" target="_blank"
                  rel="noopener">Pål-Ørjan Johansen</a>
              </li>
            </ul>
          </nav>

        </div>

        <div class="md:w-1/2 lg:w-1/3">

          <nav class="flex-1">
            <ul class="space-y-2">
              <li>
                <h2 itemprop="headline" class="font-bold text-lg text-primary-deep">{{ t('footer.contact') }}</h2>
              </li>
              <li>
                <a href="mailto:post@emmasofia.no"
                  class="flex items-center gap-3 text-foreground/90 hover:text-primary-deep underline hover:no-underline"
                  :aria-label="t('footer.email')">
                  <span class="flex size-9 items-center justify-center rounded-lg bg-primary-soft text-primary-deep">
                    <UiIcon name="mail" class="size-5" />
                  </span>
                  <span>post@emmasofia.no</span>
                </a>
              </li>
              <li>
                <a href="tel:92293108"
                  class="flex items-center gap-3 text-foreground/90 hover:text-primary-deep underline hover:no-underline"
                  :aria-label="t('footer.phone')">
                  <span class="flex size-9 items-center justify-center rounded-lg bg-primary-soft text-primary-deep">
                    <UiIcon name="phone-call" class="size-5" />
                  </span>
                  <span>922 93 108</span>
                </a>
              </li>
            </ul>

            <ul class="space-y-2 mt-8">
              <li>
                <h2 itemprop="headline" class="font-bold text-lg text-primary-deep">{{ t('footer.legal') }}</h2>
              </li>
              <li>
                <RouterLink :to="privacyPath"
                  class="flex items-center gap-3 text-foreground/90 hover:text-primary-deep underline hover:no-underline"
                  :aria-label="t('menu.privacy')">
                  <span>{{ t('menu.privacy') }}</span>
                </RouterLink>
              </li>
            </ul>
          </nav>

        </div>

      </div>

      <aside class="basis-full order-last mt-8 flex justify-center lg:justify-center">
        <p v-if="buildVersion" class="text-xs text-muted-foreground">
          Build {{ buildVersion }}
        </p>
      </aside>

    </div>

  </footer>
</template>
